import {
    addCalendarDays,
    parseDateOnlyAsUTC,
    toUTCDateOnly,
} from "@common/helpers/date";
import {
    ScheduleMilestoneAnchor,
    ScheduleMilestoneOffsetUnit,
} from "@common/types/domains/accounting/schedule";

export interface ScheduleMilestoneTimingRule {
    anchor: ScheduleMilestoneAnchor;
    offsetCount: number | null;
    offsetUnit: ScheduleMilestoneOffsetUnit | null;
    specificDate: string | null;
}

export function pickMilestoneTimingRule(
    milestone: ScheduleMilestoneTimingRule,
): ScheduleMilestoneTimingRule {
    return {
        anchor: milestone.anchor,
        offsetCount: milestone.offsetCount,
        offsetUnit: milestone.offsetUnit,
        specificDate: milestone.specificDate,
    };
}

export interface ScheduleMilestoneRuleRow extends ScheduleMilestoneTimingRule {
    sortOrder: number;
}

export interface ResolvedMilestoneDate {
    sortOrder: number;
    expectedDate: string;
}

const MONTHS_PER_UNIT: Record<
    Exclude<ScheduleMilestoneOffsetUnit, ScheduleMilestoneOffsetUnit.days>,
    number
> = {
    [ScheduleMilestoneOffsetUnit.months]: 1,
    [ScheduleMilestoneOffsetUnit.years]: 12,
};

function addCalendarMonthsUTC(options: { date: Date; months: number }): Date {
    const { date, months } = options;
    const day = date.getUTCDate();
    const target = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1),
    );
    const lastDayOfTargetMonth = new Date(
        Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
    ).getUTCDate();
    return new Date(
        Date.UTC(
            target.getUTCFullYear(),
            target.getUTCMonth(),
            Math.min(day, lastDayOfTargetMonth),
        ),
    );
}

function resolveMilestoneBaseDate(options: {
    milestone: ScheduleMilestoneTimingRule;
    sourceDate: string;
}): string {
    if (options.milestone.anchor === ScheduleMilestoneAnchor.sourceDate) {
        return options.sourceDate;
    }
    if (options.milestone.specificDate === null) {
        throw new Error("Milestone anchored on a specific date has no date");
    }
    return options.milestone.specificDate;
}

/**
 * The calendar day a milestone row lands on for one source: its base date
 * (the source date or the row's own specific date) plus the "After N <unit>"
 * offset, with month and year steps clamped to the last day of the month.
 */
export function resolveMilestoneExpectedDate(options: {
    milestone: ScheduleMilestoneTimingRule;
    sourceDate: string;
}): string {
    const baseDate = resolveMilestoneBaseDate(options);
    const { offsetCount, offsetUnit } = options.milestone;
    if (offsetCount === null || offsetUnit === null) {
        return baseDate;
    }
    if (offsetUnit === ScheduleMilestoneOffsetUnit.days) {
        return addCalendarDays(baseDate, offsetCount);
    }
    return toUTCDateOnly(
        addCalendarMonthsUTC({
            date: parseDateOnlyAsUTC(baseDate),
            months: offsetCount * MONTHS_PER_UNIT[offsetUnit],
        }),
    );
}

export function resolveMilestonePlanDates<
    T extends ScheduleMilestoneRuleRow,
>(options: {
    milestones: readonly T[];
    sourceDate: string;
    exampleDates?: readonly ResolvedMilestoneDate[];
}): Array<T & { expectedDate: string }> {
    const exampleDateBySortOrder = new Map(
        (options.exampleDates ?? []).map((example) => [
            example.sortOrder,
            example.expectedDate,
        ]),
    );
    return [...options.milestones]
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((milestone) => ({
            ...milestone,
            expectedDate:
                exampleDateBySortOrder.get(milestone.sortOrder) ??
                resolveMilestoneExpectedDate({
                    milestone,
                    sourceDate: options.sourceDate,
                }),
        }));
}
